import gameTokenLobby from "../game_token_lobby.js";
import notifications from "../global/notifications.js";
import viewHelpers from "../view_helpers.js";
import backend from "./backend.js";
import gameCloning from "./game-cloning.js";

/**
 * @typedef {object} LobbyUser
 * @property {string | number} [name]
 * @property {string} [email]
 * @property {number} [loginCount]
 * @property {string | number | null} actorId
 * @property {boolean} [isAutogen]
 * @property {string} [authentication_token]
 * @property {unknown} [lastSeen]
 */

/** @type {ReturnType<typeof setTimeout> | null | undefined} */
let autoSaveTimeout;
/**
 * @param {any} userId
 * @param {LobbyUser} userObj
 * @returns {JQuery}
 */
function composeEmptyUserEl(userId, userObj) {
	let classes = "LobbyUser j-draggable dropdown Dropdown--three-dots";
	let loginPendingTitle = "";

	// Check corresponds to to viewHelpers.generateRandomId
	if (userId < 0.5 * (2 ** 31 + 1) && !userObj.loginCount) {
		classes += " LobbyUser--first-login-pending";
		loginPendingTitle = I18n.t("user_manager.invitation_mail_sent", {
			user_email: userObj.email,
		});
	}
	const resendInvitationButonHref = `/users/${userId}/resend_invitation_email?${getResendInvitationButtonQualifier()}`;

	// Cast back `userObj.name` to String for the wonky scenario where the User name has been cast to an integer (e.g. for a name "123") by the lobby's `revivingFunction`
	return $(`<div class='${classes}' data-user-id='${userId}'>
			<span class='LobbyUser_name' title='${userObj.name}, ${userObj.email}'>${userObj.name ? String(userObj.name).truncate(20) : null}</span> 
			<span class='dropdown-toggle pull-right' type='button' data-toggle='dropdown' aria-haspopup='true' aria-expanded='false'>
				<span class='glyphicon glyphicon-option-horizontal'></span>
			</span>
			<ul class='dropdown-menu'>
				<li>
					<a class='LobbyUserExclusionButton' href='javascript:;'>
						<span class='LobbyUser--reinclude'>${I18n.t("user_manager.reinclude_user")}</span>
						<span class='LobbyUser--exclude'>${I18n.t("user_manager.exclude_user")}</span>
					</a>
					<a class='LobbyUser__resend-invitation-button' href='${resendInvitationButonHref}' data-remote='true' data-method='post'>${I18n.t("user_manager.send_reminder_email")}</a>
				</li>
			</ul>
			<span class='glyphicon glyphicon-hourglass LobbyUser__login-pending-icon pull-right' title='${loginPendingTitle}'></span>
		</div>`);
}
const emptySignuptokenEl = `–<span class='selectable pull-right'>
		<span class='senaryon-icon senaryon-icon-token'></span> <span class='j-signup-token'></span>
	</span>`;
/**
 * @param {string} authentiicationToken
 * @returns {string}
 */
function authenticationTokenElem(authentiicationToken) {
	return `<span class='pull-right'>\
			<span class='senaryon-icon senaryon-icon-token'></span>\
			<code class='j-auth-token' data-token='${authentiicationToken}'>${authentiicationToken}</code>\
		</span>`;
}
/**
 * @returns {string}
 */
function getResendInvitationButtonQualifier() {
	return $("#game_token").length
		? `game_token=${$("#game_token").val()}`
		: `game_id=${pp.currentGameId}`;
}
const assignableActorSelector = ".AssignableActor";
const lobbyUserSelector = ".LobbyUser";
const assignedActorsCountSelector = ".j-assigned-actors-count";
const totalActorsCountSelector = ".j-actors-count";
let isForClonedGame = false;
const submitButtonSelector = ".j-submit-user-assignment-changes";
/**
 * @param {any} [assignmentsForClonedGame]
 * @param {boolean} [skipDragDropInit]
 */
function init(assignmentsForClonedGame, skipDragDropInit) {
	gameTokenLobby.attachedChangeHandleInitializers.push(
		// @ts-expect-error
		addListenerForUser,
	);

	if (assignmentsForClonedGame !== undefined) {
		isForClonedGame = true;
		// Fill the "fake" gameTokenLobby with users, this needs to be decoupled from gameTokenLobby
		assignmentsForClonedGame = JSON.parse(assignmentsForClonedGame);
		Object.keys(assignmentsForClonedGame).forEach((userId) => {
			gameTokenLobby.updateLobbyUserEntryMultipleProps(
				userId,
				assignmentsForClonedGame[userId],
			);
		});
		initSubmitButtonListeners();
	}

	if (!skipDragDropInit) {
		backend.dragDrop.init(
			".j-draggable",
			".j-droppable",
			dropUserSuccessFunction,
		);
		initListeners();
	}
	// Save to Rails once on init in case some of the stored settings became invalid since the last save
	// e.g. the start_date and Actor assignemnts for Users where the Actor does not exist anymore in the new configuration
	setAutosaveTimeout();
	// update once manually for the case that not enough Users are watiing yet, in which case below `handleUserAssignmentChange` will never be called
	updateAssignmentCounts();

	// Remove handler when navigating away
	$(document).one("page:before-change", () => {
		gameTokenLobby.removeAttachedChangeHandleInitializer(addListenerForUser);
		if (!skipDragDropInit) {
			backend.dragDrop.removeListeners();
		}
		if (assignmentsForClonedGame) {
			// Need to do this here, gameTokenLobby is not being initialized if `assignmentsForClonedGame` are present
			gameTokenLobby.userData = {};
		}
	});
}

function initListeners() {
	$(".AssignableActor__vacate-button").on("click", (event) => {
		const userId = $(event.target)
			.closest(assignableActorSelector)
			.attr("data-user-id");
		gameTokenLobby.updateLobbyUserEntry(userId, "actorId", null);
		setAutosaveTimeout();
	});

	$(".j-lobby-auto-assign-users").on("click", (event) => {
		if ($(event.currentTarget).prop("disabled")) {
			return;
		}
		autoAssignUsers();
	});

	initAddUserListener();

	$(".LobbyUserExclusionButton").on("click", handleToggleInclusionClick);
}

/**
 * @param {JQuery.ClickEvent} event
 */
function handleToggleInclusionClick(event) {
	const userId = $(event.target).closest("[data-user-id]").attr("data-user-id");
	const userObj = gameTokenLobby.userData[userId];

	event.preventDefault();
	// Unassign the User if it's assigned to an Actor
	const newlyExcluded = !$(event.target)
		.closest("[data-user-id]")
		.hasClass("LobbyUser--excluded");
	if (userObj.isAutogen) {
		removeUser(userId);
		// @ts-expect-error
		gameTokenLobby.excludedGenericUserIds.push(userId);
	} else {
		gameTokenLobby.updateLobbyUserEntryMultipleProps(userId, {
			actorId: newlyExcluded ? -1 : null,
		});
	}
	setAutosaveTimeout();
}

/**
 * @param {string} userId
 * @param {boolean} [fromInitfunction]
 */
function addListenerForUser(userId, fromInitfunction = false) {
	gameTokenLobby.userData.observeChange(userId, (oldval, newval) => {
		handleUserAssignmentChange(userId, oldval, newval);
	});
	addUserToLobby(userId);
	handleUserAssignmentChange(userId, null, gameTokenLobby.userData[userId]);
	$("table.tablesorter").trigger("update");

	if (!fromInitfunction) {
		setAutosaveTimeout();
	}
}

function initAddUserListener() {
	const $addUserform = $("#add-user-modal form");
	$addUserform.off("submit").on("submit", (event) => {
		if (isForClonedGame) {
			// insert the "fake" User object into the hash
			const userProps = {
				name: $(event.currentTarget).find("input[name='user[name]']").val(),
				email: $(event.currentTarget).find("input[name='user[email]']").val(),
				actorId: null,
			};
			gameTokenLobby.updateLobbyUserEntryMultipleProps(
				viewHelpers.generateRandomId(),
				userProps,
			);
			$("#add-user-modal").modal("hide");
			// @ts-expect-error
			$addUserform[0].reset();
			return false;
		}

		// Else - submit form via AJAX and insert user data into hash
		event.preventDefault();
		$.ajax({
			url: $addUserform.attr("action"),
			type: "POST",
			dataType: "json",
			data: $addUserform.serialize(),
		})
			.done((data) => {
				gameTokenLobby.updateLobbyUserEntryMultipleProps(
					data.user_id,
					data.user_obj,
				);
				if (data.lobby_status === "lobby_full") {
					markLobbyAsFull();
				}
			})
			.fail((data) => {
				if (
					!$addUserform.find(".Field-with-errors").length &&
					data.responseJSON &&
					data.responseJSON.message
				) {
					notifications.display("error", data.responseJSON.message);
				}
			})
			.always(() => {
				if ($addUserform.find(".Field-with-errors").length) {
					return;
				}
				$("#add-user-modal").modal("hide");
				// @ts-expect-error
				$addUserform[0].reset();
				const $submitButton = $addUserform.find('input[type="submit"]');
				$submitButton
					.removeAttr("disabled")
					// @ts-expect-error
					.val($submitButton.val().replaceAll("…", ""));
			});
		$addUserform.find("#j-user-already-exists-warning").addClass("hidden");
	});

	$("#add-user-modal").on("shown.bs.modal", () => {
		$addUserform.validate();
	});
}

function markLobbyAsFull() {
	$(".button[data-target='#add-user-modal']")
		.addClass("disabled")
		.removeAttr("data-target")
		.text(I18n.t("user_manager.lobby_is_full"));
}

function initSubmitButtonListeners() {
	$(submitButtonSelector).on("click", () => {
		// Currently assigning on Rails
		if ($(submitButtonSelector).prop("disabled")) {
			return;
		}
		if ($(lobbyUserSelector).not(".LobbyUser--assigned").length) {
			$.confirm({
				text: I18n.t("user_manager.save_changes_question"),
				confirmButtonText: I18n.t("user_manager.save_changes_confirm"),
				cancelButtonText: I18n.t("helpers.rails_verbs.cancel"),
				confirm: () => {
					submitConfiguration();
				},
			});
		} else {
			submitConfiguration();
		}
	});
}

function submitConfiguration() {
	$(submitButtonSelector).attr("disabled", "disabled").prop("disabled", true);
	$(submitButtonSelector).find(".lds-ring").removeClass("hidden");
	const gameId = $(submitButtonSelector).attr("data-game-id");
	const updateConfigurationPath = `/games/${gameId}/actors/bulk_update_user_assignments.json`;
	const data = _.cloneDeep(gameTokenLobby.userData);
	delete data.observeChangeHandlers;
	$.ajax({
		url: updateConfigurationPath,
		type: "PUT",
		dataType: "json",
		data: { user_actor_assignments: data },
	})
		.done((data) => {
			notifications.display("success", data.message);
			Object.entries(data.destroyed_users_data).forEach(
				([userId, actorSignupToken]) => {
					removeUser(userId, actorSignupToken);
				},
			);
			if (Object.keys(data.converted_preliminary_user_ids).length) {
				Object.keys(data.converted_preliminary_user_ids).forEach(
					(preliminaryUserId) => {
						const newUserId =
							data.converted_preliminary_user_ids[preliminaryUserId];
						gameTokenLobby.updateLobbyUserEntryMultipleProps(
							newUserId,
							gameTokenLobby.userData[preliminaryUserId],
						);
						delete gameTokenLobby.userData[preliminaryUserId];
					},
				);
			}
			// Remove fake User ids that might still remain in the lobby
			Object.keys(gameTokenLobby.userData).forEach((userId) => {
				// Check corresponds to to viewHelpers.generateRandomId
				const id = Number(userId);
				if (Number.isNaN(id) || id < 0.5 * (2 ** 31 + 1)) {
					return;
				}
				removeUser(userId);
			});
		})
		.fail(() => {
			notifications.display(
				"error",
				I18n.t("user_manager.save_configuration_failed"),
			);
		})
		.always(() => {
			$(submitButtonSelector).removeAttr("disabled").prop("disabled", false);
			$(submitButtonSelector).find(".lds-ring").addClass("hidden");
		});
}

/**
 * @param {any} userId
 * @param {any} [actorSignupToken]
 */
function removeUser(userId, actorSignupToken) {
	delete gameTokenLobby.userData[parseInt(userId, 10)];
	const $userEls = $(`.UserActorAssigner [data-user-id='${userId}']`);
	$userEls.remove();
	const $actorWithFormerUser = $(
		`${assignableActorSelector}[data-former-user-id='${userId}']`,
	);
	$actorWithFormerUser
		.find(".AssignableActor__dropzone")
		.html(emptySignuptokenEl);
	$actorWithFormerUser.find(".j-signup-token").replaceWith(actorSignupToken);
	$actorWithFormerUser.removeAttr("data-former-user-id");
	setAutosaveTimeout();
}

/**
 * @param {JQuery} $draggedEl
 * @returns {string | undefined}
 */
function setDraggedUserId($draggedEl) {
	let draggedUserId = null;
	if ($draggedEl.parents(".LobbyUser-list").length) {
		draggedUserId = $draggedEl.attr("data-user-id");
	} else {
		draggedUserId = $draggedEl
			.closest(assignableActorSelector)
			.attr("data-user-id");
	}
	return draggedUserId;
}

/**
 * @param {JQuery} $draggedEl
 * @param {JQuery} $dropzoneEl
 */
function dropUserSuccessFunction($draggedEl, $dropzoneEl) {
	const presentUserId = $dropzoneEl
		.closest(assignableActorSelector)
		.attr("data-user-id");
	// This will return undefined if dropzone == lobby, and the user will be unassigned from their actor at
	// the end of this method
	const newActorId = $dropzoneEl
		.closest(assignableActorSelector)
		.attr("data-actor-id");
	const draggedUserId = setDraggedUserId($draggedEl);
	if (draggedUserId === undefined) {
		return;
	}
	const draggedUserExistingActorId =
		gameTokenLobby.userData[draggedUserId].actorId;

	if (draggedUserExistingActorId) {
		// Swap actors with users assigned
		markActorElAsUnassigned(draggedUserExistingActorId);
		if (presentUserId) {
			gameTokenLobby.updateLobbyUserEntry(
				presentUserId,
				"actorId",
				draggedUserExistingActorId,
			);
		}
	} else if (presentUserId) {
		//Kick existing user back to lobby i.e. swap existing user with actor assigned with lobby user
		gameTokenLobby.updateLobbyUserEntry(presentUserId, "actorId", null);
	}
	if (newActorId) {
		// drag user from lobby to actor
		gameTokenLobby.updateLobbyUserEntry(draggedUserId, "actorId", newActorId);
	} else {
		// drag user with actor assigned to lobby
		gameTokenLobby.updateLobbyUserEntry(draggedUserId, "actorId", null);
	}
	setAutosaveTimeout();
}

/**
 * @param {string | number} actorId
 * @returns {JQuery}
 */
function getUserManagerEl(actorId) {
	return $(`${assignableActorSelector}[data-actor-id='${actorId}']`);
}

/**
 * @param {string | number} actorId
 */
function markActorElAsUnassigned(actorId) {
	const $assignableActorEl = getUserManagerEl(actorId);
	const formerUserId = parseInt(
		$assignableActorEl.attr("data-user-id") ?? "",
		10,
	);
	if ($assignableActorEl.length) {
		$assignableActorEl.removeAttr("data-user-id");
	}
	if (formerUserId) {
		// Might be NaN
		$assignableActorEl.attr("data-former-user-id", formerUserId);
	}
}

/**
 * @param {any} userId
 * @param {string | number} actorId
 */
function assignUserToActorVisually(userId, actorId) {
	const userObj = gameTokenLobby.userData[userId];

	const $assignableActor = getUserManagerEl(actorId);
	const $assignedUserItem = $assignableActor.find(
		".AssignableActor__assigned-user",
	);

	$assignedUserItem.addClass("j-droppable").removeClass("hidden"); // TODO remove class in the unassign user
	$assignedUserItem
		.find(".j-assigned-user-name")
		.attr("title", `${userObj.name}, ${userObj.email}`);
	if (userObj.name) {
		let userNameContent = String(userObj.name).truncate(20);
		// Show the User's `authentication_token` it it's a autogen User *and* if the Game has already been cloned
		if (isForClonedGame && userObj.isAutogen && userObj.authentication_token) {
			userNameContent += authenticationTokenElem(userObj.authentication_token);
		}
		$assignedUserItem.find(".j-assigned-user-name").html(userNameContent);
		// Must to this after the item has been added to the DOM
		if (isForClonedGame && userObj.isAutogen && userObj.authentication_token) {
			viewHelpers.copyToClipboardListener(
				`.j-auth-token[data-token='${userObj.authentication_token}']`,
				`.j-auth-token[data-token='${userObj.authentication_token}']`,
				true,
			);
		}
	}
	// Check corresponds to to viewHelpers.generateRandomId
	if (userId < 0.5 * (2 ** 31 + 1) && !userObj.loginCount) {
		$assignedUserItem.addClass(
			"AssignableActor__assigned-user--first-login-pending",
		);
		$assignedUserItem.find(".AssignableActor__login-pending-icon").attr(
			"title",
			I18n.t("user_manager.invitation_mail_sent", {
				user_email: userObj.email,
			}),
		);
		$assignedUserItem
			.find(".LobbyUser__resend-invitation-button")
			.attr(
				"href",
				`/users/${userId}/resend_invitation_email?${getResendInvitationButtonQualifier()}`,
			);
	}
	setSortValueForUser(userId);
	$(".tablesorter").trigger("update");
}

/**
 * @param {string | number} userId
 */
function addUserToLobby(userId) {
	const userObj = gameTokenLobby.userData[userId];
	const $userEl = composeEmptyUserEl(userId, userObj);

	$userEl
		.find(".LobbyUserExclusionButton")
		.on("click", handleToggleInclusionClick);
	// Insert the new el so that the list is sorted alphabetically
	const insertBeforeNode =
		// @ts-expect-error
		Object.values($(".LobbyUser-list")[0].children).find((presentEl) => {
			return (
				$(presentEl)
					.find(".LobbyUser_name")
					.text()
					.localeCompare($userEl.find(".LobbyUser_name").text()) === 1
			);
		}) || $(".LobbyUser-list .LobbyUser-list__empty-state"); // Default to the empty state marker
	$userEl.insertBefore(insertBeforeNode);
}

/**
 * @param {JQuery} $userEl
 * @param {LobbyUser} userObj
 */
function setInclusionStateVisually($userEl, userObj) {
	// biome-ignore lint/suspicious/noDoubleEquals: actorId may be a numeric string, e.g. from data attributes or unparsed JSON
	if (userObj.actorId == -1) {
		$userEl.addClass("LobbyUser--excluded");
		$userEl.removeClass("j-draggable");
	} else {
		$userEl.removeClass("LobbyUser--excluded");
		$userEl.addClass("j-draggable");
	}
}

/**
 * @param {string | number} userId
 */
function setSortValueForUser(userId) {
	const $el = $(`.AssignableActor[data-user-id="${userId}"]`).find(
		".AssignableActor__assigned-user",
	);
	$el
		.closest("td")
		.attr("data-sort-value", gameTokenLobby.userData[userId].name);
}

function autoAssignUsers() {
	assignUsersWithoutFunctions();
	setAutosaveTimeout();
}

function assignUsersWithoutFunctions() {
	Object.keys(gameTokenLobby.userData).forEach((userId) => {
		const isUnassignableUser =
			Number.isNaN(Number(userId)) ||
			gameTokenLobby.userData[userId].actorId != null; // Either a real Actor id or -1 if excluded
		if (isUnassignableUser) {
			// userId might actually be the `observeChangeHandlers` hash
			return;
		}

		const $firstVacantActor = $(`.AssignableActor:not([data-user-id])`).first();
		if (!$firstVacantActor.length) {
			notifications.display(
				"error",
				`No available Actors left for User id ${userId}!`,
			);
			return;
		}
		gameTokenLobby.updateLobbyUserEntry(
			userId,
			"actorId",
			$firstVacantActor.attr("data-actor-id"),
		);
	});
}

/**
 * @param {string | number} userId
 * @param {LobbyUser | null} oldval
 * @param {LobbyUser} newval
 */
function handleUserAssignmentChange(userId, oldval, newval) {
	const $userEl = $(`.LobbyUser[data-user-id='${userId}']`);
	// biome-ignore lint/suspicious/noDoubleEquals: actorId may be a numeric string, e.g. from data attributes or unparsed JSON
	if (newval.actorId && newval.actorId != -1) {
		$userEl.addClass("LobbyUser--assigned");
		const $assignableActorEl = getUserManagerEl(newval.actorId);

		if ($assignableActorEl.length) {
			// Assign a user to an actor
			$assignableActorEl.attr("data-user-id", userId);
			assignUserToActorVisually(userId, newval.actorId);
			setUserOnlineClass($assignableActorEl, newval);
		} else {
			// Unassign a user from actors i.e. their actor no longer exists
			gameTokenLobby.updateLobbyUserEntry(userId, "actorId", null);
			setAutosaveTimeout();
		}
	} else {
		// Has been unassigned or got initialized without Actor id
		$userEl.removeClass("LobbyUser--assigned");
		setInclusionStateVisually($userEl, newval);
		if (oldval && oldval.actorId !== null) {
			markActorElAsUnassigned(oldval.actorId);
		}
		if ($userEl.length) {
			setUserOnlineClass($userEl, newval);
		}
	}

	updateAssignmentCounts();
}

/**
 * @param {JQuery} $el
 * @param {LobbyUser} newval
 */
function setUserOnlineClass($el, newval) {
	if (newval.lastSeen) {
		$el.addClass("user--online").removeClass("LobbyUser--first-login-pending");
		$el
			.find(".AssignableActor__assigned-user")
			.removeClass("AssignableActor__assigned-user--first-login-pending");
	} else {
		$el.removeClass("user--online");
	}
}

function updateLobbyCounts() {
	const previousUnassigned = parseInt(String($("#actors_number").val()), 10);
	const allUsers = gameTokenLobby.usersWithPropertyCount(
		"actorId",
		"!==",
		undefined,
	); // Will be either null, -1 or > 1
	const excludedUsers = gameTokenLobby.usersWithPropertyCount(
		"actorId",
		"==",
		-1,
	);
	const assignedUsers = gameTokenLobby.usersWithPropertyCount(
		"actorId",
		">",
		0,
	);

	const unassignedUsers = allUsers - assignedUsers - excludedUsers;
	const assignableUsers = allUsers - excludedUsers;

	$("#unassigned_users").val(unassignedUsers);
	if (previousUnassigned !== assignableUsers) {
		$("#actors_number").val(assignableUsers);
		$("#actors_number").trigger("keyup");
	}
	// The Actors number validator will not fire if there are no waiting Users in the lobby, so disable the cloning button this way
	else if (allUsers === 0 && !isForClonedGame) {
		$("#new_game").find("input[type='submit']").prop("disabled", true);
	}

	$("#unassigned_users").trigger("change.ClientSideValidations"); // Must trigger this first, otherwise `ClientSideValidations` will not actually validate
	if (unassignedUsers === 0) {
		// Only trigger this to set the element as valid again in case it had previously failed the validation (i.e. on form submit)
		$("#unassigned_users").trigger("focusout.ClientSideValidations");
	}
	if (unassignedUsers === 0 || $(".AssignableActor").length === 0) {
		$(".j-lobby-auto-assign-users").prop("disabled", true).addClass("disabled");
	} else {
		$(".j-lobby-auto-assign-users")
			.prop("disabled", false)
			.removeClass("disabled");
	}
}

function updateAssignmentCounts() {
	const assignedActors = $(
		".AssignableActorList .AssignableActor[data-user-id]",
	).length;
	const totalActors = $(".AssignableActorList .AssignableActor").length;

	$(assignedActorsCountSelector).text(assignedActors);
	$(totalActorsCountSelector).text(totalActors);
}

function setAutosaveTimeout() {
	if (autoSaveTimeout) {
		clearTimeout(autoSaveTimeout);
		autoSaveTimeout = null;
	}
	autoSaveTimeout = setTimeout(() => {
		if (!isForClonedGame) {
			gameCloning.saveTokenConfig(saveTokenConfigCallback);
		}

		updateLobbyCounts();
	}, 200);
}

/**
 * @param {number} [statusCode]
 * @param {string} [errorMsg]
 */
function saveTokenConfigCallback(statusCode, errorMsg) {
	gameTokenLobby.excludedGenericUserIds = []; // Assume that GameTokenLobbyUser records have been deleted
	if (!errorMsg) {
		return;
	}
	if (statusCode === 403) {
		notifications.display("error", I18n.t("global.access_denied"), {
			dismissable: true,
		});
		Turbolinks.visit("/");
	} else {
		notifications.display("error", `${I18n.t("global.error")}: ${errorMsg}`);
	}
}

export default {
	init,
	initListeners,
	markLobbyAsFull,
	updateLobbyCounts,
};
